/**
 * Language.
 *
 * Three rules this module exists to enforce:
 *
 *  1. Language belongs to the **user**, not the shop (FR51). The shop supplies a
 *     default; the person overrides it from the globe at any time.
 *  2. Arabic is a **layout** change, not a translation (FR52). `dir` comes from
 *     here so nothing has to remember it.
 *  3. Machine translation is the default and a shop **overrides** any word
 *     (FR55a). Overrides are per shop and per language, and they win.
 *
 * Resolution order, first hit wins:
 *     shop override  →  bundled translation  →  English  →  the key itself
 *
 * Returning the key rather than throwing is deliberate: a missing string should
 * show as a slightly odd label, never a blank screen or an exception in front of
 * a customer.
 */

export interface LanguageSpec {
  code: string;
  name: string;
  nativeName: string;
  isRtl: boolean;
}

export const LANGUAGES: LanguageSpec[] = [
  { code: 'en', name: 'English',   nativeName: 'English',  isRtl: false },
  { code: 'ar', name: 'Arabic',    nativeName: 'العربية',   isRtl: true  },
  { code: 'fr', name: 'French',    nativeName: 'Français', isRtl: false },
  { code: 'de', name: 'German',    nativeName: 'Deutsch',  isRtl: false },
  { code: 'nb', name: 'Norwegian', nativeName: 'Norsk',    isRtl: false },
];

export type Dictionary = Record<string, string>;

/** Shop-supplied wording, keyed by language then string key. */
export type OverrideMap = Record<string, Dictionary>;

export function language(code: string): LanguageSpec {
  return LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0]!;
}

export function direction(code: string): 'ltr' | 'rtl' {
  return language(code).isRtl ? 'rtl' : 'ltr';
}

export interface TranslatorInput {
  lang: string;
  /** Bundled translations, keyed by language. */
  dictionaries: Record<string, Dictionary>;
  /** Per-shop overrides. These win. */
  overrides?: OverrideMap;
}

export interface Translator {
  (key: string): string;
  lang: string;
  dir: 'ltr' | 'rtl';
  /** True when this key came from the shop's own wording rather than the bundle. */
  isOverridden: (key: string) => boolean;
}

export function createTranslator(input: TranslatorInput): Translator {
  const { lang, dictionaries, overrides } = input;
  const over = overrides?.[lang] ?? {};
  const dict = dictionaries[lang] ?? {};
  const fallback = dictionaries['en'] ?? {};

  const t = ((key: string): string => {
    const o = over[key];
    if (typeof o === 'string' && o.length > 0) return o;
    const d = dict[key];
    if (typeof d === 'string' && d.length > 0) return d;
    const f = fallback[key];
    if (typeof f === 'string' && f.length > 0) return f;
    return key;
  }) as Translator;

  t.lang = lang;
  t.dir = direction(lang);
  t.isOverridden = (key: string) => typeof over[key] === 'string' && over[key]!.length > 0;
  return t;
}

/**
 * Wrap a run of digits, a part number, a VIN or a plate so it reads
 * left-to-right inside right-to-left text (FR53).
 *
 * U+2066 LEFT-TO-RIGHT ISOLATE … U+2069 POP DIRECTIONAL ISOLATE. Without this,
 * `FL3Z-17D957-AA` renders reversed in an Arabic sentence — a bug that looks
 * like nonsense to an Arabic reader and is invisible to everyone else.
 */
const LRI = '⁦';
const PDI = '⁩';

export function ltrIsolate(value: string): string {
  return `${LRI}${value}${PDI}`;
}

/** Isolate only when the surrounding text is RTL, so LTR output stays clean. */
export function isolateIfRtl(value: string, lang: string): string {
  return direction(lang) === 'rtl' ? ltrIsolate(value) : value;
}
